/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Needed for redux-saga es6 generator support
// import 'babel-polyfill';

// Load the favicon, the manifest.json file and the .htaccess file
// import 'file?name=[name].[ext]!./favicon.ico';
// import '!file?name=[name].[ext]!./manifest.json';
// import 'file?name=[name].[ext]!./.htaccess';

// Import all the third party stuff
import React from "react";
import ReactDOM from "react-dom";
import * as Sentry from "@sentry/browser";
import request from "vx-commons/lib/utils/request";
import { Provider } from "react-redux";
import { Router, browserHistory } from "react-router";
import { syncHistoryWithStore } from "react-router-redux";
import { selectLocationState } from "vx-commons/lib/selectors";
import App from "./containers/App";
import createRoutes from "./routes";
import configureStore from "./store";
import initialState from "./utils/initialstate";

const start = async function() {
  const config = request("/config.json", {
    headers: { Accept: "application/json", "Content-Type": "application/json" }
  });

  const release = await request("/release.json", {
    headers: { Accept: "application/json", "Content-Type": "application/json" }
  }).then(release => {
    if (!release.err) {
      return release.data.version;
    }
  });

  await Promise.resolve(config).then(resp => {
    const conf = resp.data;
    initialState.global = initialState.global.set("config", conf);
    sessionStorage.setItem("config", JSON.stringify(conf));
    return request(`${conf.repository}/api`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      }
    }).then(api => {
      // Sentry init is performed only in production
      if (window.location.hostname !== "localhost") {
        Sentry.init({
          dsn: "https://f13fec12ea7545618282cd0918899629@sentry.io/1491069",
          release: release,
          ignoreErrors: [
            "Not enough columns",
            "fromLatLngToDivPixel",
            /Loading chunk [0-9]+ failed/,
            "Cannot read properties of undefined (reading 'toJSON')"
          ],
          environment:
            api.data.cluster ||
            `${window.location.hostname} (${process.env.NODE_ENV})`
        });
      }
    });
  });
  // Create redux store with history
  // this uses the singleton browserHistory provided by react-router
  // Optionally, this could be changed to leverage a created history
  // e.g. `const browserHistory = useRouterHistory(createBrowserHistory)();`
  const store = await configureStore(initialState, browserHistory);

  // If you use Redux devTools extension, since v2.0.1, they added an
  // `updateStore`, so any enhancers that change the store object
  // could be used with the devTools' store.
  // As this uses Redux & Redux-Saga, the `updateStore` is needed
  // if you want to `take` actions in your Sagas, dispatched from devTools.
  // DEPRECATE
  // if (window.devToolsExtension) {
  //   window.devToolsExtension.updateStore(store);
  // }

  // Sync history and store, as the react-router-redux reducer
  // is under the non-default key ("routing"), selectLocationState
  // must be provided for resolving how to retrieve the "route" in the state
  const history = syncHistoryWithStore(browserHistory, store, {
    selectLocationState: selectLocationState()
  });

  // Set up the router, wrapping all Routes in the App component
  const rootRoute = {
    component: App,
    childRoutes: createRoutes(store)
  };

  ReactDOM.render(
    <Provider store={store}>
      <Router history={history} routes={rootRoute} />
    </Provider>,
    document.getElementById("app")
  );
};

// Initialize the application
start();



// WEBPACK FOOTER //
// ./src/index.js